import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
// No notification system used in this project yet
import { faCopy, faSave, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  clonePayrate,
  createAssociationPayrate,
  deleteAssociationPayrateById,
  getAssociationPayrates,
  updateAssociationPayrateById,
} from '../../data/actions/ratesActions';
import { ICreateAssociationPayrateRequest } from '../../data/api/ratesApi';
import { IState } from '../../data/reducers/state';
import { IAssociation } from '../../types/association/Association';
import { BasketballGrade } from '../../types/common/basketballGrades';
import { RefereeCoachGrade } from '../../types/common/refereeCoachGrades';
import { ScorerGrade } from '../../types/common/scorerGrade';
import { StatisticianGrade } from '../../types/common/statisticianGrade';
import { IPayRate, IRefereeCoachRate, IRefereeRate, IScorerRate, IStatisticianRate } from '../../types/payrates/PayRate';

interface PayrateEditorProps {
  payrate?: Partial<IPayRate> | null;
  onSave?: (payrate: IPayRate) => void;
  onCancel?: () => void;
  onDelete?: (payrateId: string) => void;
  readOnly?: boolean;
  title?: string;
  isAdmin?: boolean;
  associations: IAssociation[];
  actions: {
    createAssociationPayrate: (payrate: ICreateAssociationPayrateRequest) => Promise<IPayRate>;
    updateAssociationPayrateById: (id: string, updates: Partial<ICreateAssociationPayrateRequest>) => Promise<IPayRate>;
    deleteAssociationPayrateById: (id: string) => Promise<void>;
    clonePayrate: (sourceId: string, cloneData: { name: string; associationId?: string }) => Promise<IPayRate>;
    getAssociationPayrates: () => Promise<IPayRate[]>;
  };
}

interface PayrateFormData {
  name: string;
  associationId?: string;
  supervisor?: number;
  referee?: {
    rate: Partial<IRefereeRate>;
    plusRate?: Partial<IRefereeRate>;
    apprenticeBonus?: number;
    solo: number;
  };
  scoretable?: {
    rate: Partial<IScorerRate>;
  };
  refereeCoach?: {
    rate: Partial<IRefereeCoachRate>;
  };
  statistician?: {
    rate: Partial<IStatisticianRate>;
  };
  admin?: {
    rate?: number;
  };
}

const PayrateEditor: React.FC<PayrateEditorProps> = ({
  payrate,
  onSave,
  onCancel,
  onDelete,
  associations,
  actions,
  readOnly = false,
  title,
  isAdmin = false,
}) => {
  const [formData, setFormData] = useState<PayrateFormData>({
    name: '',
    referee: {
      rate: {},
      plusRate: {},
      apprenticeBonus: undefined,
      solo: 1,
    },
    scoretable: {
      rate: {},
    },
    refereeCoach: {
      rate: {},
    },
    statistician: {
      rate: {},
    }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isReadOnly = !!readOnly || !isAdmin;
  const updateFormDataIfEditable = (updater: React.SetStateAction<PayrateFormData>) => {
    if (isReadOnly || loading) return;
    setFormData(updater);
  };
  const isExistingPayrate = !!payrate?._id;
  const isEditing = isExistingPayrate && !isReadOnly;
  const canMutate = isAdmin && !isReadOnly;
  const isFormDisabled = loading || !canMutate;
  const headerTitle = title
    ? title
    : isReadOnly
      ? `View Payrate${payrate?.name ? `: ${payrate.name}` : ''}`
      : isExistingPayrate
        ? `Edit Payrate: ${payrate?.name}`
        : 'Create New Payrate';


  useEffect(() => {
    if (payrate) {
      setFormData({
        name: payrate.name || '',
        associationId: payrate.associationId || undefined,
        supervisor: typeof payrate.supervisor === 'number' && payrate.supervisor >= 0 ? payrate.supervisor : undefined,
        referee: {
          rate: payrate.referee?.rate || {},
          plusRate: payrate.referee?.plusRate || {},
          apprenticeBonus: payrate.referee?.apprenticeBonus,
          solo: payrate.referee?.solo ?? 1,
        },
        scoretable: {
          rate: payrate.scoretable?.rate || {},
        },
        refereeCoach: {
          rate: payrate.refereeCoach?.rate || {},
        },
        statistician: {
          rate: payrate.statistician?.rate || {},
        },
        admin: payrate.admin?.rate !== undefined
          ? {
            rate: payrate.admin.rate,
          }
          : undefined,
      });
    }
  }, [payrate]);

  const sanitizeNumericMap = <T extends Record<string | number, number | undefined>>(map?: T) => {
    if (!map) return {} as Partial<T>;
    return Object.entries(map).reduce((acc, [key, value]) => {
      if (typeof value === 'number' && value >= 0) {
        acc[key as keyof T] = value;
      }
      return acc;
    }, {} as Partial<T>);
  };

  const hasEntries = (obj?: object) => !!obj && Object.keys(obj).length > 0;

  const sanitizeValue = (value?: number) =>
    typeof value === 'number' && value >= 0 ? value : undefined;

  const handleSave = async () => {
    if (isReadOnly) return;

    if (!formData.name.trim()) {
      setError('Payrate name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const supervisor = sanitizeValue(formData.supervisor);

      const refereeRate = sanitizeNumericMap(formData.referee?.rate) as Partial<IRefereeRate>;
      const refereePlusRate = sanitizeNumericMap(formData.referee?.plusRate) as Partial<IRefereeRate>;
      const refereeApprentice = sanitizeValue(formData.referee?.apprenticeBonus);
      const refereeSolo =
        typeof formData.referee?.solo === 'number' && formData.referee.solo > 0
          ? formData.referee.solo
          : undefined;


      const hasRefereeNumbers =
        hasEntries(refereeRate) ||
        hasEntries(refereePlusRate) ||
        refereeApprentice !== undefined;

      const refereeData =
        formData.referee && hasRefereeNumbers
          ? {
            rate: refereeRate,
            ...(hasEntries(refereePlusRate) ? { plusRate: refereePlusRate } : {}),
            ...(refereeApprentice !== undefined ? { apprenticeBonus: refereeApprentice } : {}),
            solo: refereeSolo ?? 1,
          }
          : undefined;

      const scoretableRate = sanitizeNumericMap(formData.scoretable?.rate) as Partial<IScorerRate>;
      const scoretableData = hasEntries(scoretableRate)
        ? {
          rate: scoretableRate,
        }
        : undefined;

      const refereeCoachRate = sanitizeNumericMap(formData.refereeCoach?.rate) as Partial<IRefereeCoachRate>;
      const refereeCoachData = hasEntries(refereeCoachRate)
        ? {
          rate: refereeCoachRate,
        }
        : undefined;

      const statisticianRate = sanitizeNumericMap(formData.statistician?.rate) as Partial<IStatisticianRate>;
      const statisticianData = hasEntries(statisticianRate)
        ? { rate: statisticianRate }
        : undefined;

      const adminRate = sanitizeValue(formData.admin?.rate);
      const adminData = adminRate !== undefined ? { rate: adminRate } : undefined;

      const payrateData: ICreateAssociationPayrateRequest = {
        name: formData.name.trim(),
        associationId: formData.associationId,
        supervisor,
        referee: refereeData,
        scoretable: scoretableData,
        refereeCoach: refereeCoachData,
        admin: adminData,
        statistician: statisticianData,
      };

      let result: IPayRate;
      if (isEditing) {
        result = await actions.updateAssociationPayrateById(payrate!._id, payrateData);
        console.log('Payrate updated successfully');
      } else {
        result = await actions.createAssociationPayrate(payrateData);
        console.log('Payrate created successfully');
      }

      if (onSave) {
        onSave(result);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save payrate');
      console.error('Failed to save payrate:', err.message || err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (isReadOnly || !isExistingPayrate || !payrate?._id) return;

    if (window.confirm('Are you sure you want to delete this payrate? This action cannot be undone.')) {
      setLoading(true);
      try {
        await actions.deleteAssociationPayrateById(payrate._id);
        console.log('Payrate deleted successfully');
        if (onDelete) {
          onDelete(payrate._id);
        }
      } catch (err: any) {
        console.error('Failed to delete payrate:', err.message || err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClone = async () => {
    if (isReadOnly || !isExistingPayrate || !payrate?._id) return;

    const cloneName = prompt('Enter name for cloned payrate:', `${payrate.name} (Copy)`);
    if (!cloneName) return;

    setLoading(true);
    try {
      const result = await actions.clonePayrate(payrate._id, {
        name: cloneName,
        associationId: formData.associationId,
      });
      console.log('Payrate cloned successfully');
      if (onSave) {
        onSave(result);
      }
    } catch (err: any) {
      console.error('Failed to clone payrate:', err.message || err);
    } finally {
      setLoading(false);
    }
  };

  const updateRefereeRate = (grade: BasketballGrade, value: number) => {
    updateFormDataIfEditable(prev => {
      const nextRate = { ...(prev.referee?.rate || {}) };
      if (typeof value === 'number' && value >= 0) {
        nextRate[grade] = value;
      } else {
        delete nextRate[grade];
      }
      return {
        ...prev,
        referee: {
          ...prev.referee!,
          rate: nextRate,
        },
      };
    });
  };

  const updateRefereePlusRate = (grade: BasketballGrade, value: number) => {
    updateFormDataIfEditable(prev => {
      const nextRate = { ...(prev.referee?.plusRate || {}) };
      if (typeof value === 'number' && value >= 0) {
        nextRate[grade] = value;
      } else {
        delete nextRate[grade];
      }
      return {
        ...prev,
        referee: {
          ...prev.referee!,
          plusRate: nextRate,
        },
      };
    });
  };

  const updateScorerRate = (grade: ScorerGrade, value: number) => {
    updateFormDataIfEditable(prev => {
      const nextRate = { ...(prev.scoretable?.rate || {}) };
      if (typeof value === 'number' && value >= 0) {
        nextRate[grade] = value;
      } else {
        delete nextRate[grade];
      }
      return {
        ...prev,
        scoretable: {
          ...prev.scoretable!,
          rate: nextRate,
        },
      };
    });
  };

  const updateRefereeCoachRate = (grade: RefereeCoachGrade, value: number) => {
    updateFormDataIfEditable(prev => {
      const nextRate = { ...(prev.refereeCoach?.rate || {}) };
      if (typeof value === 'number' && value >= 0) {
        nextRate[grade] = value;
      } else {
        delete nextRate[grade];
      }
      return {
        ...prev,
        refereeCoach: {
          ...prev.refereeCoach!,
          rate: nextRate,
        },
      };
    });
  };

  const updateStatisticianRate = (grade: StatisticianGrade, value: number) => {
    updateFormDataIfEditable(prev => {
      const nextRate = { ...(prev.statistician?.rate || {}) };
      if (typeof value === 'number' && value >= 0) {
        nextRate[grade] = value;
      } else {
        delete nextRate[grade];
      }
      return {
        ...prev,
        statistician: {
          ...prev.statistician!,
          rate: nextRate,
        },
      };
    });
  };

  const associationOptions = associations.map(assoc => ({
    value: assoc._id,
    label: assoc.name,
  }));

  const refereeGrades = BasketballGrade.all();
  const scorerGrades: ScorerGrade[] = [0, 1, 2, 3, 4, 5];
  const refereeCoachGrades: RefereeCoachGrade[] = [0, 1, 2, 3, 4, 5];
  const statisticianGrades: StatisticianGrade[] = [0, 1, 2, 3, 4];

  const formatNumber = (value?: number) => {
    if (value === null || value === undefined) return '—';
    return Number.isFinite(value) ? value.toString() : '—';
  };

  const renderRateGrid = (
    title: string,
    gradeLabels: { value: string | number; label: string }[],
    rows: Array<
      | {
        key: string;
        label: string;
        values: Record<string | number, number | undefined>;
        onChange: (grade: any, value: number) => void;
      }
      | {
        key: string;
        label: string;
        singleValue: number | undefined;
        onChange: (value: number) => void;
        placeholder?: string;
      }
    >,
  ) => (
    <Stack gap="xs" style={{ overflowX: 'auto' }}>
      <Title order={5} style={{ marginBottom: 0 }}>{title}</Title>
      <Table
        withColumnBorders
        highlightOnHover
        verticalSpacing="xs"
        horizontalSpacing="xs"
        style={{ minWidth: Math.max(520, gradeLabels.length * 100 + 160) }}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: 140 }}>Category</Table.Th>
            {gradeLabels.map((g) => (
              <Table.Th key={g.value}>{g.label}</Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => {
            if ('values' in row) {
              return (
                <Table.Tr key={row.key}>
                  <Table.Td style={{ fontWeight: 600 }}>{row.label}</Table.Td>
                  {gradeLabels.map((g) => (
                    <Table.Td key={`${row.key}-${g.value}`}>
                      {isReadOnly ? (
                        <Text size="sm">{formatNumber(row.values[g.value])}</Text>
                      ) : (
                        <NumberInput
                          size="xs"
                          value={
                            row.values[g.value] !== undefined && row.values[g.value]! >= 0
                              ? row.values[g.value]
                              : undefined
                          }
                          onChange={(value) => row.onChange(g.value, typeof value === 'number' ? value : -1)}
                          precision={2}
                          min={-1}
                          step={0.5}
                          disabled={isFormDisabled}
                        />
                      )}
                    </Table.Td>
                  ))}
                </Table.Tr>
              );
            }

            return (
              <Table.Tr key={row.key}>
                <Table.Td style={{ fontWeight: 600 }}>{row.label}</Table.Td>
                <Table.Td colSpan={gradeLabels.length}>
                  {isReadOnly ? (
                    <Text size="sm">{formatNumber(row.singleValue)}</Text>
                  ) : (
                    <NumberInput
                      size="xs"
                      value={row.singleValue !== undefined && row.singleValue >= 0 ? row.singleValue : undefined}
                      onChange={(value) => row.onChange(typeof value === 'number' ? value : -1)}
                      precision={2}
                      min={-1}
                      step={0.5}
                      disabled={isFormDisabled}
                      placeholder={row.placeholder}
                    />
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );

  return (
    <Card padding="lg" shadow="sm">
      <Stack gap="md">
        <Group justify="apart">
          <Title order={3}>{headerTitle}</Title>
          {canMutate && isExistingPayrate && (
            <Group>
              <Tooltip label="Clone Payrate">
                <ActionIcon variant="outline" onClick={handleClone} disabled={loading}>
                  <FontAwesomeIcon icon={faCopy} size="sm" />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Delete Payrate">
                <ActionIcon variant="outline" color="red" onClick={handleDelete} disabled={loading}>
                  <FontAwesomeIcon icon={faTrash} size="sm" />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </Group>

        {error && (
          <Alert color="red" title="Error">
            {error}
          </Alert>
        )}

        <Grid>
          <Grid.Col span={8}>
            {isReadOnly ? (
              <div>
                <Text size="xs" c="dimmed">Payrate Name</Text>
                <Text>{formData.name || '—'}</Text>
              </div>
            ) : (
              <TextInput
                label="Payrate Name"
                value={formData.name}
                onChange={(e) =>
                  updateFormDataIfEditable(prev => ({ ...prev, name: e.target.value }))
                }
                required
                disabled={isFormDisabled}
              />
            )}
          </Grid.Col>
          <Grid.Col span={4}>
            {isReadOnly ? (
              <div>
                <Text size="xs" c="dimmed">Association</Text>
                <Text>
                  {associationOptions.find((a) => a.value === formData.associationId)?.label || '—'}
                </Text>
              </div>
            ) : (
              <Select
                label="Association"
                value={formData.associationId}
                onChange={(value) =>
                  updateFormDataIfEditable(prev => ({ ...prev, associationId: value || undefined }))
                }
                data={associationOptions}
                clearable
                searchable
                disabled={isFormDisabled}
              />
            )}
          </Grid.Col>
        </Grid>

        {renderRateGrid(
          'Referee Rates',
          refereeGrades.map((g) => ({ value: g, label: BasketballGrade.toString(g) })),
          [
            {
              key: 'referee-regular',
              label: 'Regular',
              values: formData.referee?.rate || {},
              onChange: updateRefereeRate,
            },
            {
              key: 'referee-plus',
              label: 'Panel / Plus',
              values: formData.referee?.plusRate || {},
              onChange: updateRefereePlusRate,
            },
            {
              key: 'referee-apprentice',
              label: 'Apprentice Bonus',
              singleValue: formData.referee?.apprenticeBonus,
              onChange: (value) =>
                updateFormDataIfEditable((prev) => ({
                  ...prev,
                  referee: {
                    ...prev.referee!,
                    apprenticeBonus: typeof value === 'number' && value >= 0 ? value : undefined,
                  },
                })),
              placeholder: 'Per game',
            },
            {
              key: 'referee-solo',
              label: 'Solo Multiplier',
              singleValue: formData.referee?.solo,
              onChange: (value) =>
                updateFormDataIfEditable((prev) => ({
                  ...prev,
                  referee: { ...prev.referee!, solo: value && value > 0 ? value : 1 },
                })),
              placeholder: 'Default is 1',
            },
          ],
        )}

        <Divider />



        {renderRateGrid(
          'Referee Coach Rates',
          refereeCoachGrades.map((g) => ({ value: g, label: `Grade ${g}` })),
          [
            {
              key: 'coach-regular',
              label: 'Regular',
              values: formData.refereeCoach?.rate || {},
              onChange: updateRefereeCoachRate,
            },
          ],
        )}

        <Divider />

        {renderRateGrid(
          'Scoretable Rates',
          scorerGrades.map((g) => ({ value: g, label: `Grade ${g}` })),
          [
            {
              key: 'scoretable-regular',
              label: 'Regular',
              values: formData.scoretable?.rate || {},
              onChange: updateScorerRate,
            },
          ],
        )}

        <Divider />

        {renderRateGrid(
          'Statistician Rates',
          statisticianGrades.map((g) => ({ value: g, label: `Level ${g}` })),
          [
            {
              key: 'statistician-regular',
              label: 'Regular',
              values: formData.statistician?.rate || {},
              onChange: updateStatisticianRate,
            },
          ],
        )}

        <Divider />

        <Grid>
          <Grid.Col span={6}>
            {isReadOnly ? (
              <div>
                <Text size="xs" c="dimmed">Supervisor Rate</Text>
                <Text>{formatNumber(formData.supervisor)}</Text>
              </div>
            ) : (
              <NumberInput
                label="Supervisor Rate"
                value={
                  typeof formData.supervisor === 'number' && formData.supervisor >= 0
                    ? formData.supervisor
                    : undefined
                }
                onChange={(value) =>
                  updateFormDataIfEditable(prev => ({
                    ...prev,
                    supervisor:
                      typeof value === 'number' && value >= 0 ? value : undefined,
                  }))
                }
                precision={2}
                min={-1}
                step={0.50}
                disabled={isFormDisabled}
              />
            )}
          </Grid.Col>
          <Grid.Col span={6}>
            {isReadOnly ? (
              <div>
                <Text size="xs" c="dimmed">Admin Rate</Text>
                <Text>{formatNumber(formData.admin?.rate)}</Text>
              </div>
            ) : (
              <NumberInput
                label="Admin Rate"
                value={
                  typeof formData.admin?.rate === 'number' && formData.admin.rate >= 0
                    ? formData.admin.rate
                    : undefined
                }
                onChange={(value) =>
                  updateFormDataIfEditable(prev => ({
                    ...prev,
                    admin:
                      typeof value === 'number' && value >= 0
                        ? { rate: value }
                        : undefined,
                  }))
                }
                precision={2}
                min={-1}
                step={0.50}
                disabled={isFormDisabled}
              />
            )}
          </Grid.Col>
        </Grid>

        <Divider />

        <Group justify="right">
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            {isReadOnly ? 'Close' : 'Cancel'}
          </Button>
          {canMutate && (
            <Button onClick={handleSave} loading={loading} disabled={isFormDisabled}>
              <FontAwesomeIcon icon={faSave} className="me-2" />
              {isEditing ? 'Update' : 'Create'} Payrate
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
};

const mapStateToProps = (state: IState) => ({
  associations: state.associations.admin.map(
    (a) => state.associations.map[a],
  ).filter(Boolean),
  isAdmin: true,
});

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      createAssociationPayrate,
      updateAssociationPayrateById,
      deleteAssociationPayrateById,
      clonePayrate,
      getAssociationPayrates,
    },
    dispatch
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(PayrateEditor);
